/**
 * Program Structure 1064 — KUK Programme Structure.
 *
 * Fixed background art behind a heading + brochure CTA on the left and a single-open
 * semester accordion of subject tables on the right, with an optional rich-text
 * disclaimer below. Ported from kuk-campus-portal
 * `components/Degree/ProgramStructure.tsx`.
 *
 * One flat metadata row = one subject, the same shape Program Structure 1042 stores:
 *  - `semester_name` groups the row into a semester section;
 *  - `semester_is_elective_based` marks the whole semester "as per elective";
 *  - `credits` is stored and not drawn, because the reference's table has the credits
 *    column commented out. Showing it later needs no migration.
 *
 * Every field maps to a column that already exists on `program_structure`, which has
 * no `variant` ENUM (the variant lives on `page_component.variant`), so the variant
 * needs no migration at all.
 */

export const PROGRAM_STRUCTURE_1064_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Subjects",
  },

  DEFAULT_ROW: {
    semester_name: "Semester 1",
    semester_is_elective_based: false,
    subject_name: "",
    credits: "",
    priority: 0,
    status: true,
  },

  PROGRAM_STRUCTURE_1064_FIELDS_CONFIG: [
    {
      fieldName: "title",
      label: "Heading",
      value: "Programme Structure",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Programme Structure",
    },
    {
      fieldName: "title_text_color",
      label: "Heading + table text colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#101112",
    },
    {
      fieldName: "gradient_color_1",
      label: "Heading rule colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C33",
      description: "The short accent bar under the heading.",
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "section_background_image",
      label: "Background art (fixed)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload full-bleed background",
      description: "Parallaxes as the page scrolls, as in the reference.",
      defaultValue: "",
      filterCategory: "images",
    },
    {
      fieldName: "section_background_image_alt",
      label: "Background art alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      fieldName: "section_background_color",
      label: "Section fill (behind the art)",
      value: "#F5F5F5",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#F5F5F5",
    },
    {
      fieldName: "subject_container_color",
      label: "Subject card surface",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "header_section_color",
      label: "Table header rule colour",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "subjects_column_label",
      label: "Subject column label",
      value: "Subject",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Subject",
    },
    {
      fieldName: "electives_tab_label",
      label: "\"As per elective\" pill text",
      value: "As per elective",
      valueType: "string",
      filedType: "INPUT",
      description: "Shown beside a semester whose rows are marked elective-based.",
      defaultValue: "As per elective",
    },
    {
      fieldName: "card_background_color",
      label: "\"As per elective\" pill fill",
      value: "rgba(159, 28, 51, 0.2)",
      valueType: "string",
      filedType: "RGBA",
      defaultValue: "rgba(159, 28, 51, 0.2)",
    },
    {
      fieldName: "brochure_cta_text",
      label: "Brochure button text",
      value: "Brochure",
      valueType: "string",
      filedType: "INPUT",
      description: "Blank hides the button.",
      defaultValue: "Brochure",
    },
    {
      fieldName: "cta_button_color",
      label: "Brochure button fill",
      value: "#9F1C33",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#9F1C33",
    },
    {
      fieldName: "brochure_cta_text_color",
      label: "Brochure button label colour",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "cta_button_icon",
      label: "Brochure button icon",
      value: "download",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon, or type an icon name",
      defaultValue: "download",
    },
    {
      fieldName: "brochure_cta_is_cta",
      label: "Brochure opens the download lead form",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      description: "Off → link straight to the PDF below.",
      defaultValue: true,
    },
    {
      fieldName: "cta_button_text",
      label: "Brochure PDF (when it is not a lead form)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload or paste a PDF URL",
      defaultValue: "",
    },
    {
      fieldName: "section_description",
      label: "Disclaimer under the accordion",
      value: "",
      valueType: "string",
      filedType: "RICH_TEXT_EDITOR",
      defaultValue: "",
    },
    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Semester",
      fieldName: "semester_name",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. Semester 1",
      description: "Rows sharing a semester are grouped into one accordion section.",
      defaultValue: "",
    },
    {
      header: "As per elective",
      fieldName: "semester_is_elective_based",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      description: "Marks the whole semester, not just this row.",
      defaultValue: false,
    },
    {
      header: "Subject",
      fieldName: "subject_name",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Credits (stored, not shown)",
      fieldName: "credits",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Priority",
      fieldName: "priority",
      value: 0,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 0,
    },
    {
      header: "Status",
      fieldName: "status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS_1: [],
};
